import { Link } from '@tanstack/react-router';
import { Lock } from 'lucide-react';

/**
 * RUF-540: machine-readable keys from the shared profileCompleteness()
 * predicate (packages/shared/src/dto/client-profile.ts) — the portal owns
 * label mapping, mirrors apps/operations/src/components/shared/CompletenessBadge.tsx.
 *
 * SIL-1190: the business rule (profileCompleteness) is UNCHANGED — still
 * street/city/country only — but the individual rule
 * (individualProfileCompleteness) now requires all five address fields.
 * Both predicates use the same 'address' key, so the label must be picked
 * by the `individual` prop below rather than shared as one constant.
 */
const MISSING_FIELD_LABELS: Record<string, string> = {
  legalName: 'Legal name',
  contactEmail: 'Contact email',
  contactPhone: 'Contact phone',
  address: 'Address (street, city, country)',
  // SIL-1132: individual_profile completeness keys (individualProfileCompleteness,
  // packages/shared/src/dto/individual-profile.ts) — mirrors individual-profile.tsx's
  // own COMPLETENESS_FIELDS labels, for callers that pass individualProfileMissing
  // as `missingFields` (e.g. listings.tsx for an individual org).
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  countryOfResidence: 'Country of residence',
};

/** SIL-1190: individual-org override for the shared 'address' key — see the doc comment above. */
const INDIVIDUAL_ADDRESS_LABEL = 'Address (street, city, state, postal code, country)';

function labelFor(field: string, individual: boolean): string {
  if (individual && field === 'address') return INDIVIDUAL_ADDRESS_LABEL;
  return MISSING_FIELD_LABELS[field] ?? field;
}

/**
 * Visible-but-locked empty state (RUF-540). Rendered in place of a page body
 * (or an action surface like listing-create / offer-submit) when the org's
 * seller or buyer profile exists but is incomplete or inactive — replaces the
 * old pattern of hiding the nav item / redirecting to the dashboard.
 */
export function ProfileLockedCard({
  side,
  missingFields = [],
  // SIL-1132: an individual org's profile lives at /individual-profile, not
  // /profile (the business dual-profile editor) — callers on a route an
  // individual org can reach (e.g. listings.tsx) must override this.
  href = '/profile',
  // SIL-1190: picks the 'address' label — individual's is five fields, the
  // business rule (this component's default) is still three.
  individual = false,
}: {
  side: 'seller' | 'buyer';
  missingFields?: string[];
  href?: string;
  individual?: boolean;
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center">
      <Lock className="mx-auto mb-3 size-8 text-zinc-300" />
      <p className="text-sm font-medium text-zinc-700">
        Complete your {side} profile to unlock
      </p>
      <p className="mt-1 text-sm text-zinc-500">
        {missingFields.length > 0
          ? `Missing: ${missingFields.map((f) => labelFor(f, individual)).join(', ')}`
          : 'Your organization needs an active, complete profile to use this page.'}
      </p>
      <Link
        to={href}
        className="mt-4 inline-flex rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        Complete profile
      </Link>
    </div>
  );
}
